<template>
  <!-- eslint-disable max-len -->
  <div
    id="alert-sidebar"
    v-shortkey="['esc']"
    v-click-outside="close"
    class="sidepanel sidepanel--dark guide"
    @shortkey="close"
  >
    <div class="sidepanel-topbar">
      <div class="sidepanel-topbar__title">
        Getting Started
      </div>
      <span
        id="exit-sidepanel"
        class="sidepanel-exit"
        @click="close"
        v-html="closeIcon"
      />
    </div>
    <div class="sidepanel__description">
      Before you can use the Link Solution, you must complete the following steps.
    </div>
    <template v-if="loading">
      <loader :size="50" />
    </template>
    <template v-else>
      <div class="guide-completion">
        <div class="mr-1">
          {{ completionPercentage }}%
        </div>
        <div class="guide-completion-bar">
          <div
            class="guide-completion-bar guide-completion-bar--completed"
            :style="{ width: completionPercentage + '%' }"
          />
        </div>
      </div>
      <ul class="guide-list">
        <li
          v-for="item in guideItems"
          :key="item.label"
          :class="[{ 'guide-list__item--complete': item.complete }, 'guide-list__item']"
          @click="goToRoute(item)"
        >
          {{ item.label }}
        </li>
      </ul>
    </template>
    <div class="guide-footer">
      Need additional help?
      <template v-for="item in helpItems">
        <div
          :key="item.label"
          class="guide-footer__item"
          @click="goToLink(item)"
        >
          {{ item.label }}
          <span
            class="guide-footer__item-icon"
            v-html="chevronIcon"
          />
        </div>
      </template>
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import feather from 'feather-icons'
import gettingStartedQueries from '@/graphql/gettingStarted.js'
import Loader from '@/components/loader.vue'

export default {
  name: 'GettingStartedPanel',
  components: {
    Loader,
  },
  mixins: [gettingStartedQueries],
  data() {
    return {
      loading: false,
      guideItems: {
        deviceAdded: {
          label: 'Add a Link device',
          routeName: 'devices',
          complete: false,
        },
        deviceStepup: {
          label: 'Enable a Link Access Point',
          routeName: 'devices',
          complete: false,
        },
        authenticator: {
          label: 'Choose an authenticator',
          routeName: 'configuration',
          complete: false,
        },
        schedule: {
          label: 'Set up a Mobile Authentication schedule',
          routeName: 'schedule',
          complete: false,
        },
        userAdded: {
          label: 'Add a user',
          routeName: 'users',
          complete: false,
        },
        cardAdded: {
          label: 'Add a Mobile 2FA enabled card',
          routeName: 'users',
          complete: false,
        },
      },
      helpItems: [
        {
          label: 'Installation Guide',
          link: 'https://bioconnect.com/link/install/',
        },
        {
          label: 'Frequently Asked Questions',
          link: 'https://bioconnect.com/unified-for-doors/#faq',
        },
        {
          label: 'Contact Support',
          link: 'https://support.bioconnect.com/',
        },
      ],
      chevronIcon: feather.icons['arrow-up-right'].toSvg(),
      closeIcon: feather.icons.x.toSvg(),
    }
  },
  computed: {
    completionPercentage() {
      const HUNDRED_PERCENT = 100
      const _totalItems = Object.keys(this.guideItems).length
      const _completedItems = Object.keys(this.guideItems).filter((item) => {
        return this.guideItems[item].complete
      })
      const _completedPercentage = _completedItems.length / _totalItems
      return Math.ceil(_completedPercentage * HUNDRED_PERCENT)
    },
  },
  created() {
    this.fetchTasks()
  },
  methods: {
    async fetchTasks() {
      this.loading = true
      try {
        const _tasks = await this.fetchGuideItems()
        const {listDevices, listUsers, listSchedules, listSettings} = _tasks
        this.checkDevices(listDevices.objects)
        this.checkSettings(listSettings)
        this.checkSchedule(listSchedules.objects)
        this.checkUser(listUsers.objects)
        this.loading = false
      } catch (error) {
        this.loading = false
        console.log(error)
      }
    },
    checkDevices(devices) {
      if (devices && devices.length > 0) {
        this.guideItems.deviceAdded.complete = true
        this.guideItems.deviceStepup.complete = devices.some((device) => {
          return !device.isBypass
        })
      }
    },
    checkSettings(setting) {
      const {authenticator} = setting
      if (setting && authenticator) {
        this.guideItems.authenticator.complete = authenticator !== 'none'
      }
    },
    checkSchedule(schedule) {
      if (schedule && schedule.length > 0) {
        this.guideItems.schedule.complete = true
      }
    },
    checkUser(users) {
      const _realUsers = users.filter((user) => {
        return user.username !== 'bypassUser'
      })
      this.guideItems.userAdded.complete = _realUsers.length > 1
      this.guideItems.cardAdded.complete = _realUsers.some((user) => {
        if (user.cards.length > 0) {
          return user.cards.some((card) => {
            return !card.isBypass
          })
        }
      })
    },
    calculateCompletionPercentage() {
      const _totalItems = Object.keys(this.guideItems).length
      const _completedItems = Object.keys(this.guideItems).filter((item) => {
        return item.complete
      })
    },
    goToLink(item) {
      const {link} = item
      const _newWindow = window.open(link, '_blank')
    },
    goToRoute(guideItem) {
      const {routeName} = guideItem
      this.close()
      this.$router.push({name: routeName})
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.guide {
  @include flex-display();
  @include flex-direction(column);
  &-list {
    list-style-type: none;
    margin: 0 1em;
    padding: 2em 0;
    border-bottom: 1px solid $grey-text;
    flex-grow: 1;
    &__item {
      position: relative;
      padding: 1em 2.5em;
      transition: transform 0.2s ease-in-out;
      &:hover {
        transform: scale(1.05);
        cursor: pointer;
        color: $green;
      }
      &::before {
        content: "";
        position: absolute;
        width: 1em;
        height: 1em;
        background: $white;
        border-radius: 5px;
        left: 0;
        border: 2px solid transparent;
      }
      &--complete {
        text-decoration: line-through;
        &::before {
          background: $green;
          border: 2px solid $white;
        }
      }
    }
  }
  &-footer {
    margin-top: 2.5em;
    &__item {
      margin: 1.5em 0;
      padding: 0.5em;
      border: 1px solid $grey-text;
      border-radius: 5px;
      position: relative;
      &:hover {
        cursor: pointer;
        border-color: $green;
      }
      &-icon {
        position: absolute;
        top: 6px;
        right: 0;
        margin: auto 0.5em;
      }
    }
  }
  &-completion {
    @include flex-display();
    @include flex-align-items(center);
    margin-top: 2em;
    &-bar {
      width: 100%;
      height: 1.5em;
      background: $white;
      border-radius: 30px;
      &--completed {
        background-color: $green;
      }
    }
  }
}
</style>
